import { Injectable, inject } from '@angular/core';
import { StrictTranslation, TranslateService, TranslationObject } from '@ngx-translate/core';
import { CountryPackService } from './country-pack.service';

type Leaves = Map<string, StrictTranslation>;

/**
 * REV-17: wording that differs by country lives in the i18n files under `country.<CODE>.<key>` (the Saudi "Zakat and income tax",
 * the Egyptian "National ID"); the key itself holds the neutral wording. Once the company's pack is known, its texts replace the
 * neutral ones of the same key, so screens keep using the plain key (`'statementClass.IncomeTax' | translate`). Another company
 * (or none) restores the neutral texts first.
 */
@Injectable({ providedIn: 'root' })
export class CountryTextService {
  private readonly translate = inject(TranslateService);
  private code: string | null = null;
  /** Per language, the neutral texts the pack replaced (to put back). */
  private readonly replaced = new Map<string, Leaves>();

  constructor() {
    inject(CountryPackService).pack$.subscribe(pack => {
      if ((pack?.code ?? null) === this.code) return;
      this.code = pack?.code ?? null;
      this.apply(this.translate.getCurrentLang());
    });
    this.translate.onLangChange.subscribe(e => this.apply(e.lang));
  }

  private apply(lang: string | undefined): void {
    if (!lang) return;
    const before = this.replaced.get(lang);
    if (before?.size) this.translate.setTranslation(lang, nest(before), true);
    this.replaced.delete(lang);
    if (!this.code) return;

    const texts = this.translate.instant(`country.${this.code}`);
    if (!texts || typeof texts !== 'object') return;
    const leaves = flatten(texts as TranslationObject);
    const neutral: Leaves = new Map();
    for (const key of leaves.keys()) neutral.set(key, this.translate.instant(key));
    this.replaced.set(lang, neutral);
    this.translate.setTranslation(lang, nest(leaves), true);
  }
}

function flatten(obj: TranslationObject, prefix = '', out: Leaves = new Map()): Leaves {
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object' && !Array.isArray(v)) flatten(v as TranslationObject, key, out);
    else out.set(key, v);
  }
  return out;
}

function nest(leaves: Leaves): TranslationObject {
  const root: TranslationObject = {};
  for (const [key, value] of leaves) {
    const parts = key.split('.');
    let at = root;
    for (const p of parts.slice(0, -1)) at = (at[p] ??= {}) as TranslationObject;
    at[parts[parts.length - 1]] = value;
  }
  return root;
}
